// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Consumption history (ADR-056, 2026-09-05) — what the user actually
 * recorded as eaten, as IMMUTABLE user history.
 *
 * Identity is (user, meal date in the product's local calendar, slot) —
 * never a plan revision's meal-instance row and never a wire meal id such
 * as `breakfast_d0` (those are re-issued per revision). Every record carries
 * the snapshot taken at logging time: what the meal was, how much was
 * planned, how much was eaten and the calories/macros that were counted.
 * A later plan revision (apply, undo, rebuild) can change recommendations;
 * it can never erase, hide, reinterpret or recalculate a record.
 *
 * Canonical rule (one record counts per key): for each (user, date, slot)
 * the newest record by recordedAt (then id) is the canonical one; older
 * rows are superseded audit history (a re-record, or the pre-ADR-056
 * "hidden outcome → re-log" duplicates) and are never counted twice.
 */
import { z } from "zod";
import { IsoDateSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Same values as meals.ts's MealOutcomeStatusSchema — declared here (not
 * imported) because meals.ts → ledger.ts → history.ts would otherwise be a
 * cycle; `history-enum-parity.test.ts` pins the two enums equal.
 */
export const ConsumptionOutcomeSchema = z.enum(["had", "partial", "skipped", "changed"]);
/** How the snapshot came to be — `unavailable` fields are honest gaps, never estimates. */
export const ConsumptionSnapshotStatusSchema = z.enum(["recorded", "backfilled", "unavailable"]);
export const ConsumptionMacrosSchema = z.object({
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
});
/**
 * ADR-078 — an off-plan entry that REPLACED this slot. Declared here (not
 * imported from ledger.ts's LedgerConfidenceSchema/etc.) for the same
 * import-direction reason as ConsumptionOutcomeSchema above: ledger.ts
 * already imports FROM history.ts, so history.ts must not import back.
 * `history-enum-parity.test.ts`'s pattern covers this pairing too.
 */
export const ReplacementFoodSchema = z.object({
    entryId: z.string(),
    description: z.string(),
    kcal: z.number().int(),
    confidence: z.enum(["high", "medium", "low"]),
    /** Honest uncertainty band; kcal is the tracking value. */
    estimatedMinKcal: z.number().int().nullable(),
    estimatedMaxKcal: z.number().int().nullable(),
    proteinG: z.number().nullable(),
    carbsG: z.number().nullable(),
    fatG: z.number().nullable(),
    createdVia: z.enum(["amy", "user"]),
});
export const ConsumptionRecordSchema = z.object({
    id: z.string(),
    /** Meal date in the product's local calendar (Europe/Berlin) — the day the meal belongs to, never the logging timestamp's day. */
    date: IsoDateSchema,
    slot: z.string(),
    outcome: ConsumptionOutcomeSchema,
    recordedAt: IsoDateTimeSchema,
    /** The recipe the plan showed when the outcome was logged (provenance). */
    recipeId: z.string().nullable(),
    /** User-facing title at logging time; null when the catalog had none. */
    title: z.record(z.string(), z.string()).nullable(),
    /** Servings the plan had for the meal when it was logged. */
    plannedServings: z.number().nullable(),
    /** Absolute servings eaten when the user gave one (had/partial). */
    actualServings: z.number().nullable(),
    /** Share of the planned meal counted as eaten: had 1 (or actual/planned), partial actual/planned, skipped/changed 0. */
    consumedFraction: z.number().nullable(),
    /** Authoritative calories counted for this record — rounded once at logging; never recomputed. */
    consumedKcal: z.number().int().nullable(),
    consumedMacros: ConsumptionMacrosSchema.nullable(),
    snapshotStatus: ConsumptionSnapshotStatusSchema,
    /** Which plan revision / meal-instance row the record was logged against — provenance only, never identity. */
    provenance: z.object({
        revisionId: z.string().nullable(),
        mealInstanceId: z.string(),
    }),
    /**
     * ADR-078: the off-plan food actually eaten instead of this slot, when
     * `outcome` is `changed` via a linked off-plan entry. Present only when
     * non-empty (an omitted key, never `[]`, so pre-ADR-078 fixtures stay
     * byte-identical). A later re-record back to `had`/`skipped` keeps this on
     * the historical row — it is what happened, not the live slot's status.
     */
    replacedBy: z.array(ReplacementFoodSchema).optional(),
});
export const ConsumptionHistoryQuerySchema = z.object({
    from: IsoDateSchema,
    to: IsoDateSchema,
});
export const ConsumptionHistoryDaySchema = z.object({
    date: IsoDateSchema,
    /** Canonical records of the day (one per slot), oldest slot first by slot name. */
    meals: z.array(ConsumptionRecordSchema),
    /** Σ consumedKcal of the day's canonical records with a known snapshot. */
    consumedKcal: z.number().int(),
    /** true when at least one record's calories could not be reconstructed. */
    incomplete: z.boolean(),
});
export const ConsumptionHistoryResponseSchema = z.object({
    from: IsoDateSchema,
    to: IsoDateSchema,
    /** Only days with at least one record; ascending by date. */
    days: z.array(ConsumptionHistoryDaySchema),
});
//# sourceMappingURL=history.js.map